import {
  AlertTriangle,
  ChevronDown,
  ChevronLeft,
  ChevronUp,
  Link2,
  Loader2,
  Plus,
  Trash2,
} from 'lucide-react';
import { NumberField, SelectField, TextField } from '@trajectory/ui';
import { PanelLearningBanner } from '@/components/PanelLearningBanner';
import { TermHelp } from '@/components/TermHelp';
import { useCatalogEngines, useCatalogReferenceRockets } from '@/hooks/useCatalog';
import {
  useCreateRocket,
  useDeleteRocket,
  useRocketList,
  useRocketPerformance,
} from '@/hooks/useRockets';
import { useMissionStore } from '@/stores/missionStore';
import {
  addStage,
  loadRocketRecord,
  moveStage,
  removeStage,
  updateStage,
  useRocketStore,
} from '@/stores/rocketStore';
import { useSelectionStore } from '@/stores/selectionStore';
import { useUiStore } from '@/stores/uiStore';
import type { EngineDto, RocketDto, RocketRecord, StageDto } from '@trajectory/contracts';

function loadRocket(record: RocketRecord): void {
  loadRocketRecord(record);
}

function notifyFailure(title: string, error: unknown): void {
  useUiStore.getState().notify({
    kind: 'danger',
    title,
    message: error instanceof Error ? error.message : String(error),
  });
}

function RocketPicker() {
  const rockets = useRocketList();
  const referenceRockets = useCatalogReferenceRockets();
  const engines = useCatalogEngines();
  const createRocket = useCreateRocket();
  const deleteRocket = useDeleteRocket();
  const missionRocketId = useMissionStore((state) => state.document?.rocketId ?? null);

  const handleBlank = async () => {
    const engine = engines.data?.[0];
    if (!engine) return;
    try {
      const created = await createRocket.mutateAsync({
        name: 'New Rocket',
        payloadMassKg: 500,
        stages: [
          {
            id: crypto.randomUUID(),
            name: 'Stage 1',
            structuralMassKg: 1000,
            tankMassKg: 500,
            propellantMassKg: 10_000,
            engine,
            engineCount: 1,
            diameterMeters: 3,
            separationDelaySeconds: 4,
          },
        ],
      });
      loadRocket(created);
    } catch (error) {
      notifyFailure('Could not create the rocket', error);
    }
  };

  const handleDelete = async (rocket: RocketRecord) => {
    if (!window.confirm(`Delete rocket "${rocket.name}"? This cannot be undone.`)) return;
    try {
      await deleteRocket.mutateAsync({ id: rocket.id, version: rocket.version });
    } catch (error) {
      notifyFailure(`Could not delete "${rocket.name}"`, error);
      return;
    }
    if (useRocketStore.getState().id === rocket.id) useRocketStore.getState().clear();
    useUiStore.getState().notify({ kind: 'info', title: `Deleted "${rocket.name}"` });
  };

  const handleCloneReference = async (reference: RocketDto) => {
    try {
      const { id: _id, ...vehicle } = reference;
      const created = await createRocket.mutateAsync({
        ...vehicle,
        name: `${vehicle.name} (copy)`,
      });
      loadRocket(created);
      useUiStore.getState().notify({
        kind: 'success',
        title: `Created "${created.name}"`,
        message: 'This is an editable copy; the catalogue reference remains unchanged.',
      });
    } catch (error) {
      notifyFailure('Could not clone the reference vehicle', error);
    }
  };

  return (
    <div className="flex h-full flex-col gap-4 overflow-auto p-4 text-sm">
      <PanelLearningBanner panelId="rocket-editor" />
      <div>
        <h2 className="text-base font-semibold text-text">Rocket Editor</h2>
        <p className="mt-1 text-muted">
          Select an existing rocket, clone a reference vehicle, or start blank.
        </p>
      </div>

      <button
        type="button"
        onClick={() => void handleBlank()}
        disabled={!engines.data?.length || createRocket.isPending}
        className="flex items-center justify-center gap-2 rounded-control border border-border bg-surface-raised px-3 py-2 text-xs text-text hover:bg-surface-sunken disabled:opacity-40"
      >
        <Plus className="h-3.5 w-3.5" /> New blank rocket
      </button>

      <div>
        <h3 className="mb-1 text-xs font-medium text-muted">Your rockets</h3>
        {rockets.isLoading && <p className="text-xs text-faint">Loading…</p>}
        {rockets.isError && (
          <p role="alert" className="text-xs text-danger">
            Could not load saved rockets:{' '}
            {rockets.error instanceof Error ? rockets.error.message : String(rockets.error)}
          </p>
        )}
        {rockets.data?.length === 0 && <p className="text-xs text-faint">None yet.</p>}
        <ul className="flex flex-col gap-1">
          {rockets.data?.map((rocket) => {
            // The mission's own rocket is deleted through the mission, not
            // from under it — removing it here would leave the mission
            // pointing at nothing.
            const usedByOpenMission = rocket.id === missionRocketId;
            return (
              <li key={rocket.id} className="group flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => loadRocket(rocket)}
                  className="min-w-0 flex-1 rounded-control border border-border px-2 py-1.5 text-left text-xs text-text hover:bg-surface-sunken"
                >
                  {rocket.name}{' '}
                  <span className="text-faint">· {rocket.stages.length} stage(s)</span>
                </button>
                <button
                  type="button"
                  onClick={() => void handleDelete(rocket)}
                  disabled={usedByOpenMission || deleteRocket.isPending}
                  aria-label={`Delete rocket ${rocket.name}`}
                  title={usedByOpenMission ? 'In use by the selected mission' : 'Delete rocket'}
                  className="shrink-0 rounded-control p-1 text-faint opacity-0 hover:text-danger focus-visible:opacity-100 disabled:cursor-not-allowed disabled:opacity-0 group-hover:opacity-100"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      <div>
        <h3 className="mb-1 text-xs font-medium text-muted">Reference vehicles</h3>
        {referenceRockets.isLoading && <p className="text-xs text-faint">Loading catalogue…</p>}
        {referenceRockets.isError && (
          <p role="alert" className="text-xs text-danger">
            Reference vehicles are unavailable.
          </p>
        )}
        <ul className="flex flex-col gap-1">
          {referenceRockets.data?.map((reference) => (
            <li key={reference.id}>
              <button
                type="button"
                onClick={() => void handleCloneReference(reference.vehicle)}
                title={`Create an editable copy of ${reference.vehicle.name}; the catalogue reference is unchanged`}
                className="w-full rounded-control border border-border px-2 py-1.5 text-left text-xs text-text hover:bg-surface-sunken"
              >
                {reference.vehicle.name} <span className="text-faint">· {reference.operator}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function StageRow({
  stage,
  index,
  total,
  engines,
}: {
  stage: StageDto;
  index: number;
  total: number;
  engines: EngineDto[];
}) {
  const selection = useSelectionStore((state) => state.selection);
  const select = useSelectionStore((state) => state.select);
  const isSelected = selection?.kind === 'stage' && selection.stageId === stage.id;

  const engineOptions = engines.map((engine) => ({ value: engine.id, label: engine.name }));

  return (
    <div
      className={`rounded-panel border p-2 ${isSelected ? 'border-accent' : 'border-border'}`}
      onClick={() => select({ kind: 'stage', stageId: stage.id })}
    >
      <div className="flex items-center gap-2">
        <TextField
          label=""
          ariaLabel={`${stage.name} stage name`}
          value={stage.name}
          onChange={(name) => updateStage(stage.id, { name })}
          className="flex-1"
        />
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            moveStage(stage.id, -1);
          }}
          disabled={index === 0}
          title="Move this stage earlier in the launch sequence"
          aria-label={`Move ${stage.name} earlier`}
          className="rounded-control p-1 text-faint hover:text-text disabled:opacity-30"
        >
          <ChevronUp className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            moveStage(stage.id, 1);
          }}
          disabled={index === total - 1}
          title="Move this stage later in the launch sequence"
          aria-label={`Move ${stage.name} later`}
          className="rounded-control p-1 text-faint hover:text-text disabled:opacity-30"
        >
          <ChevronDown className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            removeStage(stage.id);
          }}
          disabled={total <= 1}
          title={
            total <= 1
              ? 'A launch vehicle must retain at least one stage'
              : `Remove ${stage.name} from the vehicle`
          }
          aria-label={`Remove ${stage.name}`}
          className="rounded-control p-1 text-faint hover:text-danger disabled:opacity-30"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="mt-2 grid grid-cols-2 gap-2">
        <SelectField
          label="Engine"
          value={stage.engine.id}
          options={engineOptions}
          hint="Changing the engine updates thrust, efficiency, propellant type, and cost inputs."
          onChange={(engineId) => {
            const engine = engines.find((candidate) => candidate.id === engineId);
            if (engine) updateStage(stage.id, { engine });
          }}
        />
        <NumberField
          label="Engine count"
          value={stage.engineCount}
          min={1}
          max={64}
          step={1}
          onChange={(engineCount) =>
            updateStage(stage.id, { engineCount: Math.round(engineCount) })
          }
        />
        <NumberField
          label={<TermHelp termId="propellant">Propellant (kg)</TermHelp>}
          value={stage.propellantMassKg}
          min={0}
          hint="Usable fuel and oxidizer carried by this stage."
          onChange={(propellantMassKg) => updateStage(stage.id, { propellantMassKg })}
        />
        <NumberField
          label={<TermHelp termId="dry-mass">Structural mass (kg)</TermHelp>}
          value={stage.structuralMassKg}
          min={0}
          hint="Dry hardware excluding tanks, propellant, and payload."
          onChange={(structuralMassKg) => updateStage(stage.id, { structuralMassKg })}
        />
        <NumberField
          label="Tank mass (kg)"
          value={stage.tankMassKg}
          min={0}
          onChange={(tankMassKg) => updateStage(stage.id, { tankMassKg })}
        />
        <NumberField
          label="Tank volume (m³)"
          value={stage.tankVolumeCubicMeters ?? 1}
          min={0.001}
          onChange={(tankVolumeCubicMeters) => updateStage(stage.id, { tankVolumeCubicMeters })}
        />
      </div>
      <div className="mt-2 rounded-control bg-surface-sunken p-2 text-[11px] text-muted">
        <span>{stage.engine.propellant}</span> ·{' '}
        <span>{(stage.engine.thrustVacuumNewtons / 1000).toFixed(0)} kN vacuum thrust</span> ·{' '}
        <span>{stage.engine.ispVacuumSeconds.toFixed(0)} s ISP</span>
      </div>
    </div>
  );
}

function RocketWorkspace() {
  const document = useRocketStore((state) => state.document);
  const rocketId = useRocketStore((state) => state.id);
  const patch = useRocketStore((state) => state.patch);
  const engines = useCatalogEngines();
  const performance = useRocketPerformance(rocketId);
  const missionRocketId = useMissionStore((state) => state.document?.rocketId ?? null);
  const missionId = useMissionStore((state) => state.id);

  if (!document) return null;

  const isLinkedToMission = missionId !== null && missionRocketId === rocketId;

  return (
    <div className="flex h-full flex-col gap-3 overflow-auto p-3 text-sm">
      <PanelLearningBanner panelId="rocket-editor" />
      <div className="flex items-center gap-2">
        {/* Without this the picker was unreachable once a rocket was open:
            nothing else in the app clears the rocket store, so the only way
            to switch or create another vehicle was a page reload. */}
        <button
          type="button"
          onClick={() => useRocketStore.getState().clear()}
          title="Back to the rocket list"
          aria-label="Back to the rocket list"
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-control text-muted hover:bg-surface-sunken hover:text-text"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <TextField
          label=""
          ariaLabel="Rocket name"
          value={document.name}
          onChange={(name) => patch({ name })}
          className="flex-1"
        />
        {missionId && (
          <button
            type="button"
            disabled={isLinkedToMission}
            onClick={() => useMissionStore.getState().patch({ rocketId })}
            title={
              isLinkedToMission
                ? 'Already the selected mission’s rocket'
                : 'Use for the selected mission'
            }
            className="flex items-center gap-1 whitespace-nowrap rounded-control border border-border px-2 py-1 text-xs text-muted hover:text-text disabled:opacity-40"
          >
            <Link2 className="h-3 w-3" />
            {isLinkedToMission ? 'Linked' : 'Link to mission'}
          </button>
        )}
      </div>

      <NumberField
        label={<TermHelp termId="payload">Payload mass (kg)</TermHelp>}
        value={document.payloadMassKg}
        min={0}
        hint="The mass this rocket is intended to deliver, excluding vehicle hardware."
        onChange={(payloadMassKg) => patch({ payloadMassKg })}
      />

      <NumberField
        label="Demonstrated reliability"
        value={document.demonstratedReliability ?? 0.95}
        min={0}
        max={1}
        step={0.001}
        hint="A screening input from 0 to 1; verify its evidence and sample size before academic or engineering use."
        onChange={(demonstratedReliability) => patch({ demonstratedReliability })}
      />

      <details
        className="rounded-panel border border-border bg-surface-raised p-2"
        open={Boolean(document.fairing)}
      >
        <summary className="cursor-pointer text-xs font-medium text-muted">
          <TermHelp termId="fairing">Fairing options</TermHelp>
        </summary>
        <div className="mt-2 flex flex-col gap-2">
          {!document.fairing ? (
            <button
              type="button"
              onClick={() =>
                patch({
                  fairing: {
                    massKg: 1000,
                    diameterMeters: 4,
                    lengthMeters: 10,
                    jettisonAltitudeMeters: 100_000,
                  },
                })
              }
              className="rounded-control border border-border px-2 py-1 text-xs text-muted hover:text-text"
            >
              Add payload fairing
            </button>
          ) : (
            <>
              <NumberField
                label="Mass (kg)"
                value={document.fairing.massKg}
                min={0}
                onChange={(massKg) => patch({ fairing: { ...document.fairing!, massKg } })}
              />
              <NumberField
                label="Diameter (m)"
                value={document.fairing.diameterMeters}
                min={0.1}
                max={30}
                onChange={(diameterMeters) =>
                  patch({ fairing: { ...document.fairing!, diameterMeters } })
                }
              />
              <NumberField
                label="Length (m)"
                value={document.fairing.lengthMeters}
                min={0.1}
                max={100}
                onChange={(lengthMeters) =>
                  patch({ fairing: { ...document.fairing!, lengthMeters } })
                }
              />
              <NumberField
                label="Jettison altitude (m)"
                value={document.fairing.jettisonAltitudeMeters}
                min={0}
                onChange={(jettisonAltitudeMeters) =>
                  patch({ fairing: { ...document.fairing!, jettisonAltitudeMeters } })
                }
              />
              <button
                type="button"
                onClick={() => patch({ fairing: undefined })}
                className="rounded-control border border-border px-2 py-1 text-xs text-danger"
              >
                Remove fairing
              </button>
            </>
          )}
        </div>
      </details>

      <div className="flex flex-col gap-2">
        {document.stages.map((stage, index) => (
          <StageRow
            key={stage.id}
            stage={stage}
            index={index}
            total={document.stages.length}
            engines={engines.data ?? []}
          />
        ))}
      </div>

      <button
        type="button"
        onClick={() => {
          const engine = engines.data?.[0];
          if (!engine) return;
          addStage({
            id: crypto.randomUUID(),
            name: `Stage ${document.stages.length + 1}`,
            structuralMassKg: 500,
            tankMassKg: 250,
            propellantMassKg: 5000,
            engine,
            engineCount: 1,
            diameterMeters: 2,
            separationDelaySeconds: 4,
          });
        }}
        disabled={!engines.data?.length || document.stages.length >= 8}
        title={
          document.stages.length >= 8
            ? 'The application supports up to eight serial stages'
            : 'Append a new stage using the first catalogue engine'
        }
        className="flex items-center justify-center gap-2 rounded-control border border-dashed border-border-strong px-3 py-2 text-xs text-muted hover:text-text disabled:opacity-40"
      >
        <Plus className="h-3.5 w-3.5" /> Add stage
      </button>

      <div className="mt-auto rounded-panel border border-border bg-surface-raised p-3 text-xs">
        {performance.isLoading && (
          <span className="flex items-center gap-2 text-muted">
            <Loader2 className="h-3 w-3 animate-spin" /> Computing performance…
          </span>
        )}
        {performance.data && (
          <div className="grid grid-cols-2 gap-1 text-muted">
            <span><TermHelp termId="delta-v">Total ΔV</TermHelp></span>
            <span className="text-right text-text">
              {(performance.data.totalDeltaVMetersPerSecond / 1000).toFixed(2)} km/s
            </span>
            <span>Total mass</span>
            <span className="text-right text-text">
              {(performance.data.totalMassKg / 1000).toFixed(1)} t
            </span>
            <span><TermHelp termId="thrust-to-weight">Liftoff T/W</TermHelp></span>
            <span className="text-right text-text">
              {performance.data.liftoffThrustToWeight.toFixed(2)}
            </span>
            <span>Orbit capable</span>
            <span
              className={`text-right ${performance.data.orbitCapable ? 'text-success' : 'text-danger'}`}
            >
              {performance.data.orbitCapable ? 'Yes' : 'No'}
            </span>
            {performance.data.warnings.map((warning) => (
              <span key={warning} className="col-span-2 mt-1 flex items-center gap-1 text-warning">
                <AlertTriangle className="h-3 w-3 shrink-0" /> {warning}
              </span>
            ))}
          </div>
        )}
        {performance.isError && (
          <p role="alert" className="text-danger">
            Could not compute vehicle performance:{' '}
            {performance.error instanceof Error
              ? performance.error.message
              : String(performance.error)}
          </p>
        )}
      </div>
    </div>
  );
}

export function RocketEditorPanel() {
  const hasRocket = useRocketStore((state) => state.document !== null);
  return hasRocket ? <RocketWorkspace /> : <RocketPicker />;
}
